import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, Col, Container, Input, Label, Row, Button, Form,
  FormFeedback, Alert, Spinner
} from 'reactstrap';
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { createSelector } from 'reselect';

import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import withRouter from "../../Components/Common/withRouter";
import { loginUser, resetLoginFlag } from "../../slices/thunks";
import logoLight from "../../assets/images/Logo_V2-2_white.png";


const selectLoginData = createSelector(
  (state: any) => state,
  (state) => ({
    user: state.Account?.user,
    error: state.Login?.error,
    errorMsg: state.Login?.errorMsg,
  })
);


const validationSchema = Yup.object({
  email: Yup.string().required("Veuillez saisir votre identifiant"),
  password: Yup.string().required("Veuillez saisir votre mot de passe"),
});


const Login = (props: any) => {
  const dispatch: any = useDispatch();
  const { user, error, errorMsg } = useSelector(selectLoginData);

  const [passwordShow, setPasswordShow] = useState(false);
  const [loader, setLoader] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const validation = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema,
    onSubmit: async (values) => {
      setLoader(true);
      setInlineError("");

      try {
        const action = await dispatch(loginUser(values, props.router.navigate));
        const isRejected = action?.meta?.requestStatus === 'rejected' ||
                          !!action?.error ||
                          action?.payload?.success === false;
        const status = action?.payload?.status ||
                      action?.error?.status ||
                      action?.payload?.response?.status ||
                      action?.error?.response?.status;

        if (isRejected || status === 401) {
          const msg = action?.payload?.message ||
                     action?.payload?.error ||
                     action?.error?.message ||
                     (status === 401 ? "Identifiants invalides." : "Identifiants invalides.");
          setInlineError(String(msg));
        }
      } catch (err: any) {
        const status = err?.response?.status || err?.status;
        const msg = status === 401
          ? "Identifiants invalides."
          : err?.response?.data?.message || err?.message || "Une erreur est survenue.";
        setInlineError(String(msg));
      } finally {
        setLoader(false);
        setTimeout(() => {
          if (!localStorage.getItem("authUser") && !inlineError) {
            setInlineError("Identifiants invalides.");
          }
        }, 80);
      }
    }
  });

  useEffect(() => {
    if (errorMsg) {
      setInlineError(error || "Identifiants invalides.");
      const timer = setTimeout(() => {
        dispatch(resetLoginFlag());
        setLoader(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [dispatch, errorMsg, error]);

  useEffect(() => {
    if (user) {
      const email = process.env.REACT_APP_DEFAULTAUTH === "firebase"
        ? user.multiFactor.user.email
        : user.user?.email;
      if (email) validation.setFieldValue("email", email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  document.title = "IPTrend | Espace Client";

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={logoLight} alt="IPTrend" height="80" />
                    </Link>
                  </div>
                  <p className="mt-3 fs-15 fw-medium">Votre espace client IPTrend</p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Bienvenue !</h5>
                      <p className="text-muted">Connectez-vous pour accéder à votre espace</p>
                    </div>

                    {error && <Alert color="danger">{error}</Alert>}
                    {inlineError && (
                      <Alert color="danger">
                        <i className="ri-error-warning-line me-2"></i>
                        {inlineError}
                      </Alert>
                    )}

                    <div className="p-2 mt-4">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                        action="#"
                      >
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">Identifiant</Label>
                          <Input
                            name="email"
                            className="form-control"
                            placeholder="Saisissez votre identifiant"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email}
                            invalid={!!(validation.touched.email && validation.errors.email)}
                          />
                          {validation.touched.email && validation.errors.email && (
                            <FormFeedback type="invalid">{validation.errors.email as string}</FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <div className="float-end">
                            <Link to="/forgot-password" className="text-muted">
                              Mot de passe oublié ?
                            </Link>
                          </div>
                          <Label className="form-label" htmlFor="password-input">
                            Mot de passe
                          </Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="password"
                              value={validation.values.password}
                              type={passwordShow ? "text" : "password"}
                              className="form-control pe-5"
                              placeholder="Saisissez votre mot de passe"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={!!(validation.touched.password && validation.errors.password)}
                              autoComplete="current-password"
                            />
                            {validation.touched.password && validation.errors.password && (
                              <FormFeedback type="invalid">{validation.errors.password as string}</FormFeedback>
                            )}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              id="password-addon"
                              onClick={() => setPasswordShow(!passwordShow)}
                            >
                              <i className="ri-eye-fill align-middle"></i>
                            </button>
                          </div>
                        </div>

                        <div className="form-check">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            value=""
                            id="auth-remember-check"
                          />
                          <Label className="form-check-label" htmlFor="auth-remember-check">
                            Se souvenir de moi
                          </Label>
                        </div>

                        <div className="mt-4">
                          <Button
                            color="success"
                            disabled={loader}
                            className="btn btn-success w-100"
                            type="submit"
                          >
                            {loader && <Spinner size="sm" className="me-2"> Chargement... </Spinner>}
                            Se connecter
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>

                <div className="mt-4 text-center">
                  <p className="mb-0 text-dark">
                    Besoin d'aide ?{" "}
                    <a href="mailto:support@iptrend.fr" className="fw-semibold text-dark text-decoration-underline">
                      Contactez le support
                    </a>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(Login);
